<template>
  <div>
    <b-modal
      v-model="showModal"
      size="xl"
      id="sale-returns-modal"
      :title="sale ? `Returns for order #${sale.id}` : 'Returns'"
      hide-footer
    >
      <div v-if="isLoading" class="text-center py-3">
        <b-spinner label="Loading..." />
      </div>

      <template v-else>
        <div v-if="returns.length" class="mb-2">
          <h6 class="mb-1">Previous returns</h6>
          <b-table
            striped
            hover
            small
            class="sale-returns-table"
            :items="returns"
            :fields="tableFields"
          >
            <template #cell(return_date)="data">
              {{ formatReturnDate(data.item.return_date) }}
            </template>
            <template #cell(units)="data">
              {{ formatUnits(data.item.units) }}
            </template>
            <template #cell(restore_to_stock)="data">
              {{ data.item.restore_to_stock ? 'Yes' : 'No' }}
            </template>
            <template #cell(refund_amount)="data">
              <span v-if="Number(data.item.refund_amount) > 0">
                {{ $formatCurrency(data.item.refund_amount, null) }}
                <small v-if="data.item.refund_method" class="text-muted">({{ data.item.refund_method }})</small>
              </span>
              <span v-else>-</span>
            </template>
            <template #cell(actions)="data">
              <span v-if="allowChange" class="action-link-group">
                <b-dropdown
                  variant="link"
                  toggle-class="text-decoration-none"
                  no-caret
                  right
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="text-body align-middle"
                    />
                  </template>
                  <b-dropdown-item @click="openEdit(data.item.id)">
                    <feather-icon icon="Edit2Icon" class="mr-50" />
                    <span>View / Edit</span>
                  </b-dropdown-item>
                  <b-dropdown-item @click="confirmDelete(data.item)">
                    <feather-icon icon="TrashIcon" class="mr-50" />
                    <span>Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </span>
              <span v-else class="text-muted">-</span>
            </template>
          </b-table>
        </div>
        <p
          v-else
          class="text-muted mb-2"
        >
          There are no returns for this order yet.
        </p>

        <template v-if="allowChange">
          <hr v-if="returns.length" class="my-2">

          <h6 class="mb-1">{{ returns.length ? 'Create another return' : 'Create a return' }}</h6>

          <b-form @submit.prevent="onSubmit">
            <b-row>
              <b-col cols="12">
                <b-form-group label="Reason" label-for="return-reason">
                  <b-form-input
                    id="return-reason"
                    v-model="form.reason"
                    placeholder="Reason for return"
                  />
                  <small v-if="v$.form.reason.$error" class="text-danger">Reason is required.</small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-form-group label="Return date" label-for="return-date">
                  <b-form-datepicker
                    id="return-date"
                    v-model="form.return_date"
                    reset-button
                  />
                  <small v-if="v$.form.return_date.$error" class="text-danger">Return date is required.</small>
                </b-form-group>
              </b-col>
            </b-row>

            <h6 class="mt-1 mb-1">Units to return</h6>
            <div
              v-for="unit in unitRows"
              :key="unit.unit_id"
              class="d-flex align-items-center mb-1 border rounded p-1"
            >
              <b-form-checkbox
                v-model="unit.selected"
                class="mr-1"
                :disabled="unit.max_quantity <= 0"
                @change="onUnitSelectionChange(unit)"
              />
              <div class="flex-grow-1 mr-1">
                <div class="font-weight-bold">{{ unit.name }}</div>
                <small class="text-muted">Remaining returnable: {{ unit.max_quantity }}</small>
              </div>
              <b-form-input
                type="number"
                min="1"
                :max="unit.max_quantity"
                step="1"
                class="unit-qty-input"
                v-model.number="unit.quantity"
                :disabled="!unit.selected || unit.max_quantity <= 0"
                style="width: 90px;"
              />
            </div>
            <small v-if="unitsError" class="text-danger d-block mb-1">{{ unitsError }}</small>

            <b-row class="mt-1">
              <b-col cols="12">
                <b-form-group label="Refund amount" label-for="return-refund-amount">
                  <b-form-input
                    id="return-refund-amount"
                    v-model.number="form.refund_amount"
                    type="number"
                    min="0"
                    :max="maxRefundable"
                    step="0.01"
                  />
                  <small class="text-muted d-block mt-25">
                    Max refund: {{ $formatCurrency(maxRefundable, null) }}
                    (order total {{ $formatCurrency(saleTotal, null) }},
                    paid {{ $formatCurrency(totalPaid, null) }},
                    already refunded {{ $formatCurrency(totalRefunded, null) }})
                  </small>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-form-group label="Refund method" label-for="return-refund-method">
                  <b-form-select
                    id="return-refund-method"
                    v-model="form.refund_method"
                    :options="paymentMethods"
                    :disabled="!form.refund_amount || form.refund_amount <= 0"
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <b-col cols="12">
                <b-form-checkbox
                  v-model="form.restore_to_stock"
                  switch
                  class="mb-1"
                >
                  Put the returned products back to stock
                </b-form-checkbox>
              </b-col>
            </b-row>

            <div class="text-right mt-2">
              <b-button variant="outline-secondary" class="mr-1" @click="showModal = false">
                Close
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="isSubmitting"
              >
                <b-spinner v-if="isSubmitting" small class="mr-25" />
                <span>Create return</span>
              </b-button>
            </div>
          </b-form>
        </template>
        <div
          v-else
          class="text-right mt-2"
        >
          <b-button
            variant="outline-secondary"
            @click="showModal = false"
          >
            Close
          </b-button>
        </div>
      </template>
    </b-modal>

    <ManageReturn
      :sale="sale"
      :return-id="selectedReturnId"
      :click-date="manageReturnClickDate"
      @saved="onReturnEdited"
    />
  </div>
</template>

<script>
import { formatCompanyDate } from '@/libs/companyTime'

export default {
  methods: {
    formatReturnDate(value) {
      return formatCompanyDate(value) || value || '-'
    },
    formatUnits(units) {
      if (!Array.isArray(units) || !units.length) {
        return '-'
      }

      return units.map((unit) => `${unit.name} x${unit.quantity}`).join(', ')
    },
  },
}
</script>

<script setup>
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import {
  BButton,
  BCol,
  BDropdown,
  BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { PAYMENT_METHODS, DEFAULT_PAYMENT_METHOD } from '@/constants/enums'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { companyTodayYmd } from '@/libs/companyTime'
import ManageReturn from '@/views/Components/ManageReturn.vue'

const props = defineProps({
  sale: {
    type: Object,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
  allowChange: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['saved'])

const store = useStore()
const instance = getCurrentInstance()
const showModal = ref(false)
const isLoading = ref(false)
const isSubmitting = ref(false)
const returns = ref([])
const returnContext = ref(null)
const unitRows = ref([])
const unitsError = ref('')
const paymentMethods = PAYMENT_METHODS
const selectedReturnId = ref(null)
const manageReturnClickDate = ref(new Date())

const form = reactive({
  reason: '',
  return_date: null,
  refund_amount: 0,
  refund_method: DEFAULT_PAYMENT_METHOD,
  restore_to_stock: false,
})

const rules = {
  form: {
    reason: { required },
    return_date: { required },
  },
}
const v$ = useVuelidate(rules, { form })

const tableFields = [
  { key: 'return_date', label: 'Date' },
  { key: 'reason', label: 'Reason' },
  { key: 'units', label: 'Units' },
  { key: 'refund_amount', label: 'Refund' },
  { key: 'restore_to_stock', label: 'Back to stock' },
  { key: 'actions', label: 'Actions', thClass: 'text-center', tdClass: 'text-center' },
]

const saleTotal = computed(() => Number(returnContext.value?.sale_total ?? 0))
const totalPaid = computed(() => Number(returnContext.value?.total_paid ?? 0))
const totalRefunded = computed(() => Number(returnContext.value?.total_refunded ?? 0))
const maxRefundable = computed(() => Number(returnContext.value?.max_refundable ?? 0))

const todayDateString = () => companyTodayYmd()

const resetForm = () => {
  form.reason = ''
  form.return_date = todayDateString()
  form.refund_amount = 0
  form.refund_method = DEFAULT_PAYMENT_METHOD
  form.restore_to_stock = false
  unitsError.value = ''
  v$.value.$reset()
}

const buildUnitRows = (context) => {
  unitRows.value = (context?.units || []).map((unit) => {
    const maxQuantity = Number(unit.remaining_returnable_quantity ?? 0)

    return {
      unit_id: unit.id,
      name: unit.name,
      max_quantity: maxQuantity,
      selected: maxQuantity > 0,
      quantity: maxQuantity > 0 ? maxQuantity : 1,
    }
  })
}

const emitSavedFlags = (context) => {
  emit('saved', {
    has_returns: !!context?.has_returns,
    has_any_returned: !!context?.has_any_returned,
    has_remaining_to_return: !!context?.has_remaining_to_return,
  })
}

const loadModalData = async () => {
  if (!props.sale?.id) {
    return
  }

  isLoading.value = true
  try {
    returnContext.value = await store.dispatch('returns/loadReturnContext', {
      saleId: props.sale.id,
    })
    returns.value = await store.dispatch('returns/loadBySale', props.sale.id)
    resetForm()
    buildUnitRows(returnContext.value)
    showModal.value = true
  } catch (error) {
    showModal.value = false
    toastFailure(backendErrorString(error))
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.clickDate,
  () => {
    if (!props.sale) {
      return
    }
    loadModalData()
  },
)

const onUnitSelectionChange = (unit) => {
  if (unit.selected && (!unit.quantity || unit.quantity < 1)) {
    unit.quantity = unit.max_quantity > 0 ? unit.max_quantity : 1
  }
}

const validateUnits = () => {
  const selectedUnits = unitRows.value.filter((unit) => unit.selected && unit.quantity > 0)
  if (!selectedUnits.length) {
    unitsError.value = 'Select at least one unit with quantity greater than zero.'
    return false
  }

  for (const unit of selectedUnits) {
    if (unit.quantity > unit.max_quantity) {
      unitsError.value = `Quantity for ${unit.name} cannot exceed ${unit.max_quantity}.`
      return false
    }
  }

  unitsError.value = ''
  return true
}

const openEdit = (returnId) => {
  selectedReturnId.value = returnId
  manageReturnClickDate.value = new Date()
}

const confirmDelete = async (productReturn) => {
  const confirmed = await instance.proxy.$bvModal.msgBoxConfirm(
    `Delete return${productReturn.reason ? ` "${productReturn.reason}"` : ''}?`,
    {
      title: 'Please Confirm',
      okVariant: 'danger',
      okTitle: 'Delete',
      cancelTitle: 'Cancel',
    },
  )

  if (!confirmed) {
    return
  }

  try {
    await store.dispatch('returns/deleteReturn', productReturn.id)
    toastSuccess('Return deleted successfully.')
    await loadModalData()
    emitSavedFlags(returnContext.value)
  } catch (error) {
    toastFailure(backendErrorString(error))
  }
}

const onReturnEdited = async (payload) => {
  await loadModalData()
  emit('saved', payload)
}

const onSubmit = async () => {
  if (isSubmitting.value) {
    return
  }

  const isFormCorrect = await v$.value.form.$validate()
  if (!isFormCorrect || !validateUnits()) {
    return
  }

  const refundAmount = Number(form.refund_amount || 0)
  if (refundAmount > maxRefundable.value) {
    toastFailure(`Refund amount cannot exceed ${maxRefundable.value}.`)
    return
  }

  if (refundAmount > 0 && !form.refund_method) {
    toastFailure('Refund method is required when refund amount is greater than zero.')
    return
  }

  isSubmitting.value = true

  const payload = {
    reason: form.reason,
    return_date: form.return_date,
    refund_amount: refundAmount,
    refund_method: refundAmount > 0 ? form.refund_method : null,
    restore_to_stock: form.restore_to_stock,
    units: unitRows.value
      .filter((unit) => unit.selected && unit.quantity > 0)
      .map((unit) => ({
        unit_id: unit.unit_id,
        quantity: unit.quantity,
        selected: true,
      })),
  }

  try {
    await store.dispatch('returns/createReturn', {
      saleId: props.sale.id,
      payload,
    })
    toastSuccess('Return created successfully.')
    await loadModalData()
    emitSavedFlags(returnContext.value)
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style lang="scss" scoped>
.unit-qty-input {
  flex-shrink: 0;
}

/*
 * Keep first column (Date) and last (Actions); progressively
 * hide middle columns as the viewport narrows.
 */
@mixin hide-middle-from($start) {
  :deep(thead > tr > th:nth-child(n + #{$start}):not(:last-child)),
  :deep(tbody > tr > td:nth-child(n + #{$start}):not(:last-child)) {
    display: none !important;
  }
}

.sale-returns-table {
  @media (max-width: 991.98px) {
    @include hide-middle-from(5);
  }

  @media (max-width: 767.98px) {
    @include hide-middle-from(4);
  }

  @media (max-width: 575.98px) {
    @include hide-middle-from(2);
  }
}
</style>
